// Service for managing tasks using Firebase Firestore
import {
  addTask as firestoreAddTask,
  getTasks as firestoreGetTasks,
  updateTask as firestoreUpdateTask,
  deleteTask as firestoreDeleteTask,
  Task
} from '../firebase/firestore';
import { onSnapshot, collection, query, orderBy } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';
import { db, auth } from '../firebase/firebase-config';

// Ensure user is authenticated (anonymously) before accessing Firestore
const ensureAuth = async (): Promise<void> => {
  // Skip authentication for now - Firebase rules should be set to allow public access
  // or you need to enable anonymous auth in Firebase Console
  console.log('🔓 Skipping Firebase authentication - ensure Firestore rules allow public access');
  return;
  
  /* To enable authentication later:
  if (!auth.currentUser) {
    console.log('🔐 No Firebase user detected, signing in anonymously...');
    try {
      await signInAnonymously(auth);
      console.log('✅ Anonymous Firebase authentication successful');
    } catch (error) {
      console.error('❌ Firebase anonymous auth failed:', error);
      throw error;
    }
  }
  */
};

// Re-export the Task interface from firestore
export type { Task };

// Subscribe to real-time task updates
export const subscribeToTasks = async (
  callback: (tasks: Task[]) => void, 
  onError?: (error: any) => void
): Promise<(() => void)> => {
  try {
    await ensureAuth();
    console.log('🔐 Firebase auth confirmed, setting up real-time listener...');
    
    const q = query(collection(db, "tasks"), orderBy("createdAt", "desc"));
    
    return onSnapshot(q, (snapshot) => {
      console.log('🔄 Firebase snapshot received, docs count:', snapshot.docs.length);
      const tasks = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📄 Processing task doc:', doc.id, data);
        return {
          id: doc.id,
          ...data
        } as Task;
      });
      console.log('✅ Processed tasks:', tasks.length);
      callback(tasks);
    }, (error) => {
      console.error('❌ Firebase onSnapshot error:', error);
      if (onError) {
        onError(error);
      }
    });
  } catch (error) {
    console.error('❌ Failed to set up Firebase subscription:', error);
    if (onError) {
      onError(error);
    }
    // Return a no-op unsubscribe function
    return () => {};
  }
};

// Add new task
export const addTask = async (task: Omit<Task, 'id' | '_id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  await ensureAuth();
  return await firestoreAddTask(task);
};

// Update task
export const updateTask = async (id: string, updates: Partial<Task>): Promise<void> => {
  await ensureAuth();
  return await firestoreUpdateTask(id, updates);
};

// Delete task
export const deleteTask = async (id: string): Promise<void> => {
  await ensureAuth();
  return await firestoreDeleteTask(id);
};

// Get all tasks (one-time fetch)
export const getAllTasks = async (): Promise<Task[]> => {
  await ensureAuth();
  return await firestoreGetTasks();
};

// Update task status
export const updateTaskStatus = async (id: string, status: Task['status']): Promise<void> => {
  await updateTask(id, { status });
};

// Update task priority
export const updateTaskPriority = async (id: string, priority: Task['priority']): Promise<void> => {
  await updateTask(id, { priority });
};